import { PropertyType } from 'esa-ui-library/lib/types';

export const getFiltersUrl = (filters: PropertyType[]): string => {
	const urlFragments = filters.map((f) => `${f.name}=${f.value}`);
	return `?${urlFragments.join('&')}`;
};

export const filterUsers = (users: PropertyType[], filters: PropertyType[]): PropertyType[] => {
	if (!users || !users.length) return [];
	if (!filters || !filters.length) return users;

	const activeFilter = filters.find((f) => f.name === 'active')?.value;
	const rolesFilter = filters.find((f) => f.name === 'roles')?.value;
	const permissionsFilter = filters.find((f) => f.name === 'permissions')?.value;
	const partnersFilter = filters.find((f) => f.name === 'partners')?.value;
	const qFilter = filters.find((f) => f.name === 'q')?.value;
	const searchTypeFilter = filters.find((f) => f.name === 'searchType')?.value || 'all';

	return users.filter((user) => {
		// 1. Active / Inactive filter
		if (activeFilter !== undefined && activeFilter !== '') {
			const activeValues = activeFilter.split(',');
			const userActiveState = user.state !== undefined ? `${user.state}` : user.isActive ? '1' : '0';
			if (!activeValues.includes(userActiveState)) {
				return false;
			}
		}

		// 2. Roles filter
		if (rolesFilter) {
			const selectedRoles = rolesFilter.split(',');
			const userRoles = Array.isArray(user.roles) ? user.roles : [];
			const hasRole = userRoles.some((r: PropertyType) => {
				const roleName = typeof r === 'string' ? r : r.name || r.value || r.label;
				const roleId = typeof r === 'object' ? `${r.id || ''}` : '';
				return selectedRoles.includes(roleName) || (roleId && selectedRoles.includes(roleId));
			});
			if (!hasRole) return false;
		}

		// 3. Permissions filter
		if (permissionsFilter) {
			const selectedPermissions = permissionsFilter.split(',');
			const userPerms = user.permissions || user.props || {};
			const hasPerm = selectedPermissions.some((perm: string) => {
				if (Array.isArray(userPerms)) return userPerms.includes(perm);
				return !!(userPerms as Record<string, any>)[perm];
			});
			if (!hasPerm) return false;
		}

		// 4. Partners filter
		if (partnersFilter) {
			const selectedPartners = partnersFilter.split(',');
			const userPartners = user.partners || user.groups || user.activePartners || [];
			const hasPartner = Array.isArray(userPartners)
				? userPartners.some((p: PropertyType) => {
						const pVal = typeof p === 'object' ? `${p.value || p.gid || p.id || ''}` : `${p}`;
						return selectedPartners.includes(pVal);
				  })
				: selectedPartners.includes(`${user.group_id || user.partner_id || ''}`);
			if (!hasPartner) return false;
		}

		// 5. Search filter (q and searchType)
		if (qFilter && qFilter.trim() !== '') {
			const query = qFilter.trim().toLowerCase();
			if (searchTypeFilter === 'username') {
				if (!user.username || !user.username.toLowerCase().includes(query)) return false;
			} else if (searchTypeFilter === 'email') {
				if (!user.email || !user.email.toLowerCase().includes(query)) return false;
			} else {
				// 'all' or default
				const matchUsername = user.username && user.username.toLowerCase().includes(query);
				const matchEmail = user.email && user.email.toLowerCase().includes(query);
				const matchId = user.user_id && `${user.user_id}`.toLowerCase().includes(query);
				if (!matchUsername && !matchEmail && !matchId) return false;
			}
		}

		return true;
	});
};

export const formatUsersForExport = (users: PropertyType[]): PropertyType[] => {
	return users.map((u) => {
		const rolesStr = Array.isArray(u.roles)
			? u.roles
					.map((r: PropertyType) => (typeof r === 'object' ? r.name || r.label || r.value : r))
					.filter(Boolean)
					.join(', ')
			: u.roles || '';

		return {
			user_id: u.user_id,
			username: u.username,
			email: u.email,
			roles: rolesStr,
		};
	});
};
